/** @file tileSave: Save of a Tile tab: upload the PNG and one slot.fill, or slot.clear for a blank canvas. */
import type { Command } from '@zilo/pose3d-commands/types/command';
import { encodePng } from '@zilo/pose3d-image-io/png/encodePng';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { webZlibCodec } from '@zilo/pose3d-image-io/zlib/webZlibCodec';
import type { ArtDeps } from '../../panels/art/artTypes';
import type { EditorStore } from '../../state/editorTypes';
import { tileIsBlank } from './tileIsBlank';

/**
 * A fully transparent `image` dispatches `slot.clear` (an angle never painted has no image); any other is encoded
 * (`encodePng` with `webZlibCodec`), uploaded, and filled with one `slot.fill`. One batch labelled `Paint <slot>`;
 * the store rebuilds the preview after the commit. Resolves whether the batch committed; an upload failure is a toast
 * (a refused batch already toasts in the store).
 */
export async function tileSave(store: EditorStore, upload: ArtDeps['uploadImage'], slot: string, image: Image): Promise<boolean> {
  const puppetId = store.getState().puppet.id;
  if (puppetId === null) return false;
  const blank = tileIsBlank(image);
  let commands: Command[];
  try {
    commands = blank
      ? [{ name: 'slot.clear', input: { slot } }]
      : [{ name: 'slot.fill', input: { slot, image: (await upload(puppetId, await encodePng(image, { codec: webZlibCodec }))).id } }];
  } catch (e) {
    store.getState().notify('error', `Save ${slot}: upload failed (${e instanceof Error ? e.message : String(e)})`);
    return false;
  }
  try {
    const replies = await store.getState().dispatch(commands, `Paint ${slot}`);
    return replies.every((r) => r.ok);
  } catch {
    return false;
  }
}
